// Student data now lives on the server (private per teacher). This module keeps
// the SAME function signatures the screens already use, but backs them with an
// in-memory cache that is loaded from the server on login and written through to
// the server on every change. That keeps the screens unchanged.
//
// Writes are optimistic: the cache updates immediately and the server save runs
// in the background. IDs are real UUIDs so the browser and database agree.

export interface User { id: string; firstName: string; lastName: string; email: string; trialStart: number; isPaid: boolean; school?: string; isAdmin?: boolean; hasBilling?: boolean; everPaid?: boolean; cancelAtPeriodEnd?: boolean; subscriptionEndsAt?: number | null; role?: string | null; accountType?: 'teacher' | 'parent'; }
// Parent Mode: an account is a parent iff its role is 'parent'. Everything else
// (teacher professional sub-roles, or null) is a teacher — existing accounts
// default to teacher with no migration.
export const isParent = (user: User | null | undefined): boolean => user?.accountType === 'parent' || user?.role === 'parent';
export interface Conversation { id: string; studentId: string; date: number; topic: string; input: string; followUps: string[]; response: any; }
export interface StudentProfile { id: string; initials: string; grade: string; userId: string; createdAt: number; conversations: Conversation[]; wins: Win[]; trend: 'improving'|'stable'|'needs-attention'; }
export interface Win { id: string; studentId: string; date: number; description: string; }
export interface BurnoutEntry { date: number; energy: number; connection: number; supported: number; }
export interface FridayDebrief { date: number; wentWell: string; unresolved: string; needsAttention: string; teacherStatus: string; selfCare: string; }

export interface StrategyAttempt {
  id: string;
  studentId: string;
  userId: string;
  strategy: string;
  category: 'top3' | 'academic' | 'behavioral' | 'relational';
  dateStarted: number;
  checkInDate: number;
  status: 'in-progress' | 'worked' | 'partially' | 'didnt-work';
  nextStrategy?: string;
  conversationTopic: string;
}

export interface DailyRating {
  id: string;
  strategyAttemptId: string;
  studentId: string;
  userId: string;
  date: number;
  dateKey: string; // YYYY-MM-DD
  result: 'worked' | 'partially' | 'didnt-work';
}

// Pure helpers on the user object (no storage needed).
export const isTrialExpired = (user: User): boolean => { if (user.isPaid) return false; return Date.now() - user.trialStart > 7 * 24 * 60 * 60 * 1000; };
export const getDaysLeft = (user: User): number => { const msLeft = 7*24*60*60*1000 - (Date.now() - user.trialStart); return Math.max(0, Math.ceil(msLeft / (24*60*60*1000))); };
export const generateId = () => crypto.randomUUID();

// ---- server-backed cache ----
interface Cache { students: StudentProfile[]; burnout: BurnoutEntry[]; strategies: StrategyAttempt[]; ratings: DailyRating[]; debriefs: FridayDebrief[]; }
let cache: Cache = { students: [], burnout: [], strategies: [], ratings: [], debriefs: [] };

async function apiPost(body: any) {
  try {
    const res = await fetch('/.netlify/functions/data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) console.error('data save failed', res.status);
  } catch (e) {
    console.error('data save error', e);
  }
}

// Load everything for the signed-in teacher into the cache. Call after login.
export async function loadData(): Promise<void> {
  try {
    const res = await fetch('/.netlify/functions/data', { credentials: 'include' });
    if (!res.ok) throw new Error(`load ${res.status}`);
    const d = await res.json();
    cache = {
      students: Array.isArray(d.students) ? d.students : [],
      burnout: Array.isArray(d.burnout) ? d.burnout : [],
      strategies: Array.isArray(d.strategies) ? d.strategies : [],
      ratings: Array.isArray(d.ratings) ? d.ratings : [],
      debriefs: Array.isArray(d.debriefs) ? d.debriefs : [],
    };
  } catch (e) {
    console.error('loadData failed', e);
    cache = { students: [], burnout: [], strategies: [], ratings: [], debriefs: [] };
  }
}

export function clearData(): void {
  cache = { students: [], burnout: [], strategies: [], ratings: [], debriefs: [] };
}

// ---- students / conversations / wins ----
export const getStudents = (_uid?: string): StudentProfile[] => cache.students;
export const saveStudent = (_uid: string, s: StudentProfile) => {
  const i = cache.students.findIndex(x => x.id === s.id);
  if (i >= 0) cache.students[i] = s; else cache.students.push(s);
  apiPost({ action: 'saveStudent', student: s });
};
export const getStudentById = (_uid: string, sid: string) => cache.students.find(s => s.id === sid);
export const findStudentByInitials = (_uid: string, initials: string, grade: string) =>
  cache.students.find(s => s.initials.toLowerCase() === initials.toLowerCase() && s.grade.toLowerCase() === grade.toLowerCase());

// ---- burnout ----
export const getBurnoutEntries = (_uid?: string): BurnoutEntry[] => cache.burnout;
export const saveBurnoutEntry = (_uid: string, e: BurnoutEntry) => {
  cache.burnout.push(e);
  apiPost({ action: 'saveBurnout', entry: e });
};

// ---- Friday debriefs (now server-backed like everything else) ----
export const getDebriefs = (_uid?: string): FridayDebrief[] => cache.debriefs;
export const saveDebrief = (_uid: string, d: FridayDebrief) => {
  cache.debriefs.push(d);
  apiPost({ action: 'saveDebrief', debrief: d });
};

// ---- strategy attempts ----
export const getStrategyAttempts = (_uid?: string): StrategyAttempt[] => cache.strategies;
export const saveStrategyAttempt = (_uid: string, attempt: StrategyAttempt) => {
  const i = cache.strategies.findIndex(x => x.id === attempt.id);
  if (i >= 0) cache.strategies[i] = attempt; else cache.strategies.push(attempt);
  apiPost({ action: 'saveStrategy', attempt });
};
export const getPendingCheckIns = (_uid?: string): StrategyAttempt[] => {
  const now = Date.now();
  return cache.strategies.filter(a => a.status === 'in-progress' && now >= a.checkInDate);
};
export const getStudentStrategies = (_uid: string, studentId: string): StrategyAttempt[] =>
  cache.strategies.filter(a => a.studentId === studentId);

// ---- daily ratings ----
export const getDailyRatings = (_uid?: string): DailyRating[] => cache.ratings;
export const saveDailyRating = (_uid: string, rating: DailyRating) => {
  const idx = cache.ratings.findIndex(r => r.strategyAttemptId === rating.strategyAttemptId && r.dateKey === rating.dateKey);
  if (idx >= 0) cache.ratings[idx] = rating; else cache.ratings.push(rating);
  apiPost({ action: 'saveRating', rating });
};
export const getStrategyRatings = (_uid: string, strategyAttemptId: string): DailyRating[] =>
  cache.ratings.filter(r => r.strategyAttemptId === strategyAttemptId);

export const getTodayDateKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const getStrategyScore = (_uid: string, strategyAttemptId: string): { worked: number; total: number; pct: number } => {
  const ratings = getStrategyRatings(_uid, strategyAttemptId);
  const worked = ratings.filter(r => r.result === 'worked' || r.result === 'partially').length;
  return { worked, total: ratings.length, pct: ratings.length > 0 ? Math.round((worked / ratings.length) * 100) : 0 };
};

export const getStrategiesNeedingRatingToday = (_uid?: string): StrategyAttempt[] => {
  const today = getTodayDateKey();
  const rated = cache.ratings.filter(r => r.dateKey === today).map(r => r.strategyAttemptId);
  return cache.strategies.filter(a => a.status === 'in-progress' && !rated.includes(a.id));
};
